import React, { useEffect, useRef } from 'react';

const ManufacturingPage = () => {
  const observerRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach(el => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(30px)';
      observerRef.current.observe(el);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const manufacturingUnits = [
    {
      title: "Aluminium Circles/Blanks",
      icon: "fas fa-circle",
      capacity: "2,160 Tonnes/Year",
      personnel: "50+ Personnel",
      description: "State of the art facility with fully equipped lab, three 4-Hi mills, two annealing ovens. Employing over 50 personnel in rolling departments.",
      videoSrc: "/videos/bb-aluminium-circles-production.mp4",
      imageSrc: "/images/aluminium-circles-facility.jpg",
      specifications: [
        "Can manufacture sheets of all tempers and composition",
        "Thickness Range: 0.5mm - 4mm", 
        "Size Range: 2.5\" - 48\"",
        "Advanced quality control systems"
      ],
      equipment: [
        "Three 4-Hi Rolling Mills",
        "Two Annealing Ovens", 
        "Fully Equipped Laboratory",
        "Quality Control Systems"
      ]
    },
    {
      title: "Aluminium Utensils",
      icon: "fas fa-kitchen-set",
      capacity: "300 Tonnes/Year",
      personnel: "40+ Personnel", 
      description: "Advanced manufacturing facility with comprehensive spinning and pressing capabilities for high-quality aluminium utensils production.",
      videoSrc: "/videos/bb-aluminium-utensils-production.mp4",
      imageSrc: "/images/aluminium-utensils-facility.jpg",
      specifications: [
        "Over 20 spinning machines",
        "4 pressing machines",
        "Multiple finishing processes", 
        "Precision manufacturing"
      ],
      equipment: [
        "20+ Spinning Machines",
        "4 Hydraulic Presses",
        "Finishing Equipment",
        "Quality Testing Units"
      ]
    },
    {
      title: "Steel Utensils", 
      icon: "fas fa-hammer",
      capacity: "120 Tonnes/Year",
      personnel: "New Unit",
      description: "A new manufacturing unit equipped with hydraulic press and other cutting, bending and polishing machines for steel utensil production.",
      videoSrc: "/videos/bb-steel-utensils-production.mp4", 
      imageSrc: "/images/steel-utensils-facility.jpg",
      specifications: [
        "Hydraulic press systems",
        "Precision cutting technology",
        "Advanced bending machinery",
        "Professional polishing equipment"
      ],
      equipment: [
        "Hydraulic Press Systems",
        "Cutting Machines",
        "Bending Equipment", 
        "Polishing Machines"
      ]
    }
  ];

  const capabilities = [
    {
      icon: "fas fa-cogs",
      title: "Advanced Manufacturing",
      description: "State-of-the-art equipment and processes ensuring precision and quality in every product"
    },
    {
      icon: "fas fa-flask",
      title: "Quality Laboratory",
      description: "Comprehensive testing and quality assurance protocols maintaining the highest standards"
    },
    {
      icon: "fas fa-users",
      title: "Skilled Workforce", 
      description: "Over 90 trained personnel across all manufacturing departments ensuring operational excellence"
    },
    {
      icon: "fas fa-leaf",
      title: "Sustainable Production",
      description: "Environmentally conscious manufacturing processes with efficient energy utilization"
    }
  ];

  return (
    <div className="manufacturing-page">
      <style jsx>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .manufacturing-page {
          font-family: 'Inter', 'Poppins', sans-serif;
          line-height: 1.6;
          color: #1E2329;
          overflow-x: hidden;
        }

        /* Hero Section */
        .hero {
          background: linear-gradient(135deg, #5A1FB3 0%, #1E2329 100%);
          padding: 120px 0 80px;
          position: relative;
          overflow: hidden;
        }

        .hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100" fill="%23ffffff" opacity="0.05"><polygon points="0,0 1000,0 1000,100 0,80"/></svg>');
          background-size: cover;
        }

        .hero-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
          text-align: center;
          position: relative;
          z-index: 2;
        }

        .hero h1 {
          font-size: 3.5rem;
          font-weight: 800;
          color: white;
          margin-bottom: 1.5rem;
          line-height: 1.2;
        }

        .hero-subtitle {
          font-size: 1.3rem;
          color: rgba(255, 255, 255, 0.9);
          max-width: 800px;
          margin: 0 auto 2rem;
          font-weight: 400;
        }

        .hero-icon {
          width: 80px;
          height: 80px;
          background: #7B3FE4;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 2rem;
          box-shadow: 0 10px 30px rgba(123, 63, 228, 0.3);
        }

        .hero-icon i {
          font-size: 2rem;
          color: white;
        }

        /* Manufacturing Units Section */
        .units-section {
          padding: 100px 0;
          background: linear-gradient(135deg, #F6F7F9 0%, #C9CDD3 100%);
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
        }

        .section-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .section-header h2 {
          font-size: 2.8rem;
          font-weight: 700;
          background: linear-gradient(135deg, #7B3FE4 0%, #5A1FB3 100%);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 1rem;
        }

        .section-header p {
          font-size: 1.2rem;
          color: #4A5260;
          max-width: 600px;
          margin: 0 auto;
        }

        .units-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 4rem;
        }

        .unit-card {
          background: white;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
          transition: all 0.5s ease;
          position: relative;
        }

        .unit-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 30px 80px rgba(0, 0, 0, 0.15);
        }

        .unit-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 5px;
          background: linear-gradient(90deg, #7B3FE4, #9A60FF);
        }

        .unit-layout {
          display: grid;
          grid-template-columns: 1fr;
          gap: 0;
        }

        .unit-media {
          position: relative;
          overflow: hidden;
        }

        .unit-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: all 0.3s ease;
        }

        .unit-card:hover .unit-image {
          transform: scale(1.05);
        }

        .unit-content {
          padding: 3rem;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .unit-header {
          display: flex;
          align-items: center;
          margin-bottom: 1.5rem;
        }

        .unit-icon {
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #7B3FE4, #9A60FF);
          border-radius: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1rem;
          transition: all 0.3s ease;
        }

        .unit-icon:hover {
          transform: rotate(5deg) scale(1.1);
        }

        .unit-icon i {
          font-size: 1.5rem;
          color: white;
        }

        .unit-title {
          font-size: 1.8rem;
          font-weight: 700;
          color: #1E2329;
        }

        .unit-stats {
          display: flex;
          gap: 2rem;
          margin-bottom: 1.5rem;
        }

        .stat-item {
          text-align: center;
        }

        .stat-number {
          display: block;
          font-size: 1.1rem;
          font-weight: 700;
          background: linear-gradient(135deg, #7B3FE4, #9A60FF);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .stat-label {
          font-size: 0.9rem;
          color: #4A5260;
          margin-top: 0.25rem;
        }

        .unit-description {
          color: #4A5260;
          line-height: 1.7;
          margin-bottom: 2rem;
        }

        .specifications-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
        }

        .spec-list h4 {
          font-size: 1rem;
          font-weight: 600;
          color: #7B3FE4;
          margin-bottom: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .spec-list ul {
          list-style: none;
        }

        .spec-list li {
          padding: 0.25rem 0;
          color: #4A5260;
          position: relative;
          padding-left: 1rem;
        }

        .spec-list li::before {
          content: '•';
          position: absolute;
          left: 0;
          color: #7B3FE4;
          font-weight: bold;
        }

        /* Capabilities Section */
        .capabilities-section {
          padding: 100px 0;
          background: white;
        }

        .capabilities-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 2rem;
        }

        .capability-card {
          background: #F6F7F9;
          padding: 2.5rem;
          border-radius: 15px;
          text-align: center;
          transition: all 0.3s ease;
          border: 2px solid transparent;
        }

        .capability-card:hover {
          border-color: #7B3FE4;
          transform: translateY(-5px);
          box-shadow: 0 15px 40px rgba(123, 63, 228, 0.15);
        }

        .capability-icon {
          width: 70px;
          height: 70px;
          background: linear-gradient(135deg, #9A60FF, #14B87A);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
        }

        .capability-icon i {
          font-size: 1.8rem;
          color: white;
        }

        .capability-card h3 {
          font-size: 1.3rem;
          font-weight: 600;
          color: #1E2329;
          margin-bottom: 1rem;
        }

        .capability-card p {
          color: #4A5260;
          line-height: 1.6;
        }

        /* Drone Gallery Section */
        .drone-gallery {
          padding: 100px 0;
          background: linear-gradient(135deg, #1E2329 0%, #5A1FB3 100%);
          color: white;
        }

        .drone-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .drone-header h2 {
          font-size: 2.8rem;
          font-weight: 700;
          margin-bottom: 1rem;
          background: linear-gradient(135deg, #9A60FF, #14B87A);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .drone-header p {
          font-size: 1.2rem;
          opacity: 0.9;
          max-width: 600px;
          margin: 0 auto;
        }

        .drone-images {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 2rem;
        }

        .drone-image-container {
          position: relative;
          border-radius: 15px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          transition: all 0.3s ease;
        }

        .drone-image-container:hover {
          transform: translateY(-10px);
          box-shadow: 0 30px 80px rgba(0, 0, 0, 0.4);
        }

        .drone-image {
          width: 100%;
          height: 250px;
          object-fit: cover;
        }

        .image-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
          color: white;
          padding: 1.5rem;
          transform: translateY(100%);
          transition: transform 0.3s ease;
        }

        .drone-image-container:hover .image-overlay {
          transform: translateY(0);
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .hero h1 {
            font-size: 2.5rem;
          }

          .hero-subtitle {
            font-size: 1.1rem;
          }

          .unit-layout {
            grid-template-columns: 1fr;
          }

          .unit-stats {
            justify-content: center;
          }

          .specifications-grid {
            grid-template-columns: 1fr;
          }

          .section-header h2 {
            font-size: 2.2rem;
          }

          .container {
            padding: 0 1rem;
          }
        }

        /* Animations */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fadeInUp 0.8s ease-out;
        }
      `}</style>

      {/* Add Font Awesome and Google Fonts */}
      <link href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.7.2/css/all.min.css" rel="stylesheet" />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-icon animate-fade-in">
            <i className="fas fa-industry"></i>
          </div>
          <h1 className="animate-fade-in">Manufacturing Excellence</h1>
          <p className="hero-subtitle animate-fade-in">
            State-of-the-art facilities in Balangir, Odisha delivering precision manufacturing across aluminium and steel products
          </p>
        </div>
      </section>

      {/* Manufacturing Units Section */}
      <section className="units-section">
        <div className="container">
          <div className="section-header">
            <h2>Our Manufacturing Units</h2>
            <p>Advanced production facilities equipped with cutting-edge technology and skilled workforce</p>
          </div>
          
          <div className="units-grid">
            {manufacturingUnits.map((unit, index) => (
              <div key={index} className="unit-card animate-on-scroll">
                <div className="unit-layout">
                  {/* Image/video temporarily disabled until real facility photos are available.
                  <div className="unit-media">
                    <img className="unit-image" src={unit.imageSrc} alt={unit.title + " Manufacturing"} loading="lazy" />
                  </div>
                  */}
                  <div className="unit-content">
                    <div className="unit-header">
                      <div className="unit-icon">
                        <i className={unit.icon}></i>
                      </div>
                      <h3 className="unit-title">{unit.title}</h3>
                    </div>
                    
                    <div className="unit-stats">
                      <div className="stat-item">
                        <span className="stat-number">{unit.capacity}</span>
                        <div className="stat-label">Capacity</div>
                      </div>
                      <div className="stat-item">
                        <span className="stat-number">{unit.personnel}</span>
                        <div className="stat-label">Personnel</div>
                      </div>
                    </div>
                    
                    <p className="unit-description">{unit.description}</p>
                    
                    <div className="specifications-grid">
                      <div className="spec-list">
                        <h4>Specifications</h4>
                        <ul>
                          {unit.specifications.map((spec, i) => (
                            <li key={i}>{spec}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="spec-list">
                        <h4>Equipment</h4>
                        <ul>
                          {unit.equipment.map((equip, i) => (
                            <li key={i}>{equip}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="capabilities-section">
        <div className="container">
          <div className="section-header">
            <h2>Manufacturing Capabilities</h2>
            <p>Comprehensive production capabilities backed by advanced technology and quality assurance</p>
          </div>
          
          <div className="capabilities-grid">
            {capabilities.map((capability, index) => (
              <div key={index} className="capability-card animate-on-scroll">
                <div className="capability-icon">
                  <i className={capability.icon}></i>
                </div>
                <h3>{capability.title}</h3>
                <p>{capability.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


    </div>
  );
};

export default ManufacturingPage;